import { useMemo, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { Link } from 'react-router';
import { Plus } from 'lucide-react';
import type { ColumnDef, SortingState, Updater } from '@tanstack/react-table';
import { graphql } from '@/gql';
import type { DeviceAction, DeviceStatus } from '@/gql/graphql';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataGrid } from '@/components/ui/DataGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { FormField } from '@/components/ui/FormLayout';
import { useSession } from '@/features/session/useSession';
import { Can } from '@/lib/permissions';
import { useListHref } from '@/hooks/useListLocation';
import {
  isDefault,
  optionsOf,
  sortingFromUrl,
  sortingToUrl,
  useDebouncedUrlField,
  useUrlState,
} from '@/hooks/useUrlState';
import {
  DEVICE_COLUMN_SORT,
  DEVICE_CONNECTION_LABELS,
  DEVICE_STATUS_LABELS,
  DEVICES_ADVANCED_KEYS,
  DEVICES_DEFAULT_SORT,
  devicesUrlSchema,
} from './devicesUrlState';
import { formatDateTime } from '@/lib/format';
import { downloadAuthenticatedCsv } from '@/lib/downloadCsv';
import {
  DevicePairingModal,
  type DevicePairingResult,
} from './DevicePairingModal';

const DevicesDoc = graphql(/* GraphQL */ `
  query Devices(
    $filter: DeviceFilter
    $includeInactive: Boolean
    $page: Int
    $pageSize: Int
    $sortField: String
    $sortDir: String
  ) {
    devices(
      filter: $filter
      includeInactive: $includeInactive
      page: $page
      pageSize: $pageSize
      sortField: $sortField
      sortDir: $sortDir
    ) {
      nodes {
        id
        name
        status
        connectionStatus
        createdAt
        isActive
        askLoyalty
        gratuitySettings {
          enabled
        }
        allowedActions
        category {
          id
          name
        }
        terminal {
          id
          name
        }
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

const TerminalOptionsDoc = graphql(/* GraphQL */ `
  query TerminalsForDeviceFilter {
    terminals(filter: {}, includeInactive: true, page: 1, pageSize: 500) {
      nodes {
        id
        name
      }
    }
  }
`);

const CategoryOptionsDoc = graphql(/* GraphQL */ `
  query CategoriesForDeviceFilter {
    categories(includeInactive: true) {
      id
      name
    }
  }
`);

const BulkDeviceActionDoc = graphql(/* GraphQL */ `
  mutation BulkPerformDeviceAction($ids: [ID!]!, $action: DeviceAction!) {
    bulkPerformDeviceAction(ids: $ids, action: $action) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`);

const ExportDevicesCsvDoc = graphql(/* GraphQL */ `
  query ExportDevicesCsv($filter: DeviceFilter, $sortField: String, $sortDir: String) {
    exportDevicesCsv(filter: $filter, sortField: $sortField, sortDir: $sortDir) {
      url
      expiresAt
    }
  }
`);

const CreateDeviceDoc = graphql(/* GraphQL */ `
  mutation CreateDevice($input: CreateDeviceInput!) {
    createDevice(input: $input) {
      pairingCode
      expiresAt
      device {
        id
        name
      }
    }
  }
`);

const filterSectionTitle =
  'mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';
const filterLabel = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200';

interface DeviceRow {
  id: string;
  name: string;
  status: DeviceStatus;
  connectionStatus: string;
  createdAt: string;
  isActive: boolean;
  gratuityEnabled: boolean;
  askLoyalty: boolean;
  allowedActions: DeviceAction[];
  category?: { id: string; name: string } | null;
  terminal?: { id: string; name: string } | null;
}

const STATUS_TONE: Record<DeviceStatus, BadgeTone> = {
  REQUESTING: 'warning',
  CHECKOUT: 'info',
  CANCELED: 'error',
};

function connectionBadgeTone(status: string): BadgeTone {
  switch (status.toLowerCase()) {
    case 'connected':
      return 'success';
    case 'screensaver':
      return 'warning';
    default:
      return 'neutral';
  }
}

export function DevicesListPage() {
  const apollo = useApolloClient();
  const { me } = useSession();
  const scopeId =
    me?.currentScope?.id ?? me?.defaultScope?.id ?? me?.allowedScopes[0]?.id;
  const { state: url, patch, setFilters, reset } = useUrlState(devicesUrlSchema);
  const listHref = useListHref();
  const [search, setSearch] = useDebouncedUrlField(url.search, (next) => setFilters({ search: next }));
  // The Filters panel opens on mount when a filter inside it is set (a reload, Back, an inbound link).
  const [filtersOpen] = useState(() => DEVICES_ADVANCED_KEYS.some((key) => !isDefault(devicesUrlSchema, url, key)));
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [feedback, setFeedback] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [newDeviceCategoryId, setNewDeviceCategoryId] = useState('');
  const [pairingResult, setPairingResult] = useState<DevicePairingResult | null>(
    null,
  );
  const [pairingModalOpen, setPairingModalOpen] = useState(false);

  const sorting = useMemo(() => sortingFromUrl(url), [url]);
  const handleSortingChange = (updater: Updater<SortingState>) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setFilters(sortingToUrl(next, DEVICES_DEFAULT_SORT));
  };

  const sortField = DEVICE_COLUMN_SORT[url.sort];
  const sortDir = url.dir === 'desc' ? 'DESC' : 'ASC';

  const filter = useMemo(
    () => ({
      search: url.search || null,
      status: url.status ? [url.status] : null,
      connection: url.connection ? [url.connection] : null,
      terminalIds: url.terminal ? [url.terminal] : null,
      categoryIds: url.category ? [url.category] : null,
    }),
    [url],
  );

  const { data, previousData, loading, refetch } = useQuery(DevicesDoc, {
    variables: { filter, includeInactive: true, page: url.page, pageSize: url.size, sortField, sortDir },
  });
  const devicesData = data ?? previousData;
  const { data: terminalsData } = useQuery(TerminalOptionsDoc);
  const { data: categoriesData } = useQuery(CategoryOptionsDoc);
  const [bulkPerformDeviceAction] = useMutation(BulkDeviceActionDoc);
  const [createDevice, { loading: creating }] = useMutation(CreateDeviceDoc);

  const rows: DeviceRow[] = useMemo(
    () =>
      (devicesData?.devices.nodes ?? []).map((n) => ({
        id: n.id,
        name: n.name,
        status: n.status,
        connectionStatus: n.connectionStatus,
        createdAt: n.createdAt,
        isActive: n.isActive,
        gratuityEnabled: n.gratuitySettings.enabled,
        askLoyalty: n.askLoyalty,
        allowedActions: n.allowedActions,
        category: n.category
          ? { id: n.category.id, name: n.category.name }
          : null,
        terminal: n.terminal
          ? { id: n.terminal.id, name: n.terminal.name }
          : null,
      })),
    [devicesData],
  );
  const pageInfo = devicesData?.devices.pageInfo;

  const columns = useMemo<ColumnDef<DeviceRow>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/devices/${row.original.id}`}
            state={{ from: listHref }}
            className="font-medium text-gray-900 hover:text-brand dark:text-white"
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge tone={STATUS_TONE[row.original.status] ?? 'neutral'}>
            {row.original.status.toLowerCase()}
          </Badge>
        ),
      },
      {
        id: 'connection',
        accessorFn: (row) => row.connectionStatus,
        header: 'Connection',
        cell: ({ row }) => (
          <Badge tone={connectionBadgeTone(row.original.connectionStatus)}>
            {row.original.connectionStatus.toLowerCase()}
          </Badge>
        ),
      },
      {
        id: 'category',
        accessorFn: (row) => row.category?.name ?? '',
        header: 'Category',
        cell: ({ row }) => {
          const c = row.original.category;
          if (!c) return '—';
          return (
            <Link
              to={`/categories/${c.id}`}
              className="font-medium text-gray-900 hover:text-brand dark:text-white"
            >
              {c.name}
            </Link>
          );
        },
      },
      {
        id: 'terminal',
        accessorFn: (row) => row.terminal?.name ?? '',
        header: 'Terminal',
        cell: ({ row }) => {
          const t = row.original.terminal;
          if (!t) return '—';
          return (
            <Link
              to={`/terminals/${t.id}`}
              className="font-medium text-gray-900 hover:text-brand dark:text-white"
            >
              {t.name}
            </Link>
          );
        },
      },
      {
        id: 'gratuityEnabled',
        accessorKey: 'gratuityEnabled',
        header: 'Ask tip',
        cell: ({ row }) => (row.original.gratuityEnabled ? 'Yes' : 'No'),
      },
      {
        id: 'askLoyalty',
        accessorKey: 'askLoyalty',
        header: 'Ask loyalty',
        cell: ({ row }) => (row.original.askLoyalty ? 'Yes' : 'No'),
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
    ],
    [listHref],
  );

  const handleExport = async () => {
    const { data: exportData } = await apollo.query({
      query: ExportDevicesCsvDoc,
      variables: { filter, sortField, sortDir },
      fetchPolicy: 'network-only',
    });
    if (exportData?.exportDevicesCsv?.url) {
      await downloadAuthenticatedCsv(exportData.exportDevicesCsv.url);
    }
  };

  const runBulk = async (ids: string[], action: DeviceAction) => {
    const result = await bulkPerformDeviceAction({
      variables: { ids, action },
    });
    const succeeded = result.data?.bulkPerformDeviceAction.succeeded.length ?? 0;
    const failed = result.data?.bulkPerformDeviceAction.failed.length ?? 0;
    const LABELS: Partial<Record<DeviceAction, string>> = {
      APPROVE_AS_CHECKOUT: 'Allow checkout',
      DENY: 'Deny request',
      SET_ACTIVE: 'Activate devices',
      SET_INACTIVE: 'Deactivate devices',
      SET_ASK_GRATUITY_ON: 'Enable ask tip',
      SET_ASK_GRATUITY_OFF: 'Disable ask tip',
      SET_ASK_LOYALTY_ON: 'Enable ask loyalty',
      SET_ASK_LOYALTY_OFF: 'Disable ask loyalty',
      RESET_DEVICE: 'Reset devices',
    };
    const label =
      LABELS[action] ?? action.replaceAll('_', ' ').toLowerCase();
    setFeedback(
      failed === 0
        ? `${label}: ${succeeded} updated.`
        : `${label}: ${succeeded} succeeded, ${failed} failed.`,
    );
    setSelection({});
    await refetch();
    window.setTimeout(() => setFeedback(null), 4000);
  };

  const terminalOptions = useMemo(
    () => [
      { value: '', label: 'Any terminal' },
      ...(terminalsData?.terminals.nodes.map((t) => ({
        value: t.id,
        label: t.name,
      })) ?? []),
    ],
    [terminalsData],
  );
  const categoryOptions = useMemo(
    () => [
      { value: '', label: 'Any category' },
      ...(categoriesData?.categories.map((c) => ({
        value: c.id,
        label: c.name,
      })) ?? []),
    ],
    [categoriesData],
  );

  const submitCreateDevice = async () => {
    if (creating || !newDeviceName.trim() || !scopeId) return;
    const result = await createDevice({
      variables: {
        input: {
          name: newDeviceName.trim(),
          scopeId,
          categoryId: newDeviceCategoryId || null,
          role: 'CHECKOUT',
        },
      },
    });
    const pairing = result.data?.createDevice;
    if (!pairing) return;
    setCreateModalOpen(false);
    setNewDeviceName('');
    setNewDeviceCategoryId('');
    setPairingResult({
      deviceName: pairing.device.name,
      pairingCode: pairing.pairingCode,
      expiresAt: pairing.expiresAt,
    });
    setPairingModalOpen(true);
    setSelection({});
    await refetch();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Devices
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            POS devices for the current scope. Add a device here, then enter the
            8-character pairing code on the device. Re-pair from the device detail page.
          </p>
        </div>
        <Can permission="device:create">
          <Button
            id="admin.devices.add"
            leftIcon={<Plus className="h-4 w-4" />}
            disabled={!scopeId}
            onClick={() => setCreateModalOpen(true)}
          >
            Add device
          </Button>
        </Can>
      </div>

      {feedback && (
        <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-2 text-sm text-brand-700 dark:border-brand-800 dark:bg-brand-900/40 dark:text-brand-200">
          {feedback}
        </div>
      )}

      <FilterBar
        defaultOpen={filtersOpen}
        search={{
          id: 'admin.devices.search',
          value: search,
          onChange: setSearch,
          placeholder: 'Search devices…',
        }}
        primary={
          <button
            type="button"
            onClick={reset}
            className="whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-xs transition-colors hover:bg-gray-50 dark:border-white/10 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            Reset all filters
          </button>
        }
        advanced={
          <div className="w-full min-w-0 space-y-10">
            <section className="min-w-0" aria-labelledby="devices-filters-status-heading">
              <h3 id="devices-filters-status-heading" className={filterSectionTitle}>
                Status and connection
              </h3>
              <div className="grid w-full grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-12">
                <label className="min-w-0 sm:col-span-1 lg:col-span-3">
                  <span className={filterLabel}>Status</span>
                  <Select
                    id="admin.devices.filter.status"
                    className="w-full min-w-0"
                    value={url.status}
                    onChange={(event) => setFilters({ status: event.target.value as DeviceStatus | '' })}
                    options={optionsOf(DEVICE_STATUS_LABELS, 'Any status')}
                  />
                </label>
                <label className="min-w-0 sm:col-span-1 lg:col-span-3">
                  <span className={filterLabel}>Connection</span>
                  <Select
                    id="admin.devices.filter.connection"
                    className="w-full min-w-0"
                    value={url.connection}
                    onChange={(event) => setFilters({ connection: event.target.value as keyof typeof DEVICE_CONNECTION_LABELS | '' })}
                    options={optionsOf(DEVICE_CONNECTION_LABELS, 'Any')}
                  />
                </label>
              </div>
            </section>

            <section
              className="min-w-0 border-t border-gray-200/90 pt-8 dark:border-white/10"
              aria-labelledby="devices-filters-assignment-heading"
            >
              <h3 id="devices-filters-assignment-heading" className={filterSectionTitle}>
                Assignment
              </h3>
              <div className="grid w-full grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-12">
                <label className="min-w-0 sm:col-span-1 lg:col-span-3">
                  <span className={filterLabel}>Terminal</span>
                  <Select
                    id="admin.devices.filter.terminal"
                    className="w-full min-w-0"
                    value={url.terminal}
                    onChange={(event) => setFilters({ terminal: event.target.value })}
                    options={terminalOptions}
                  />
                </label>
                <label className="min-w-0 sm:col-span-1 lg:col-span-3">
                  <span className={filterLabel}>Category</span>
                  <Select
                    id="admin.devices.filter.category"
                    className="w-full min-w-0"
                    value={url.category}
                    onChange={(event) => setFilters({ category: event.target.value })}
                    options={categoryOptions}
                  />
                </label>
              </div>
            </section>
          </div>
        }
      />

      <DataGrid<DeviceRow, DeviceAction>
        data={rows}
        columns={columns}
        loading={loading && !devicesData}
        rowId={(row) => row.id}
        sorting={{ state: sorting, onChange: handleSortingChange }}
        onExport={handleExport}
        selection={{ state: selection, onChange: setSelection }}
        allowedActionsForRow={(row) => row.allowedActions}
        actions={[
          {
            id: 'APPROVE_AS_CHECKOUT',
            label: 'Allow checkout',
            variant: 'outline',
            onRun: (selected) =>
              runBulk(
                selected.map((r) => r.id),
                'APPROVE_AS_CHECKOUT',
              ),
          },
          {
            id: 'DENY',
            label: 'Deny request',
            variant: 'danger',
            onRun: (selected) =>
              runBulk(
                selected.map((r) => r.id),
                'DENY',
              ),
          },
          {
            id: 'SET_ACTIVE',
            label: 'Activate',
            variant: 'outline',
            isAllowed: (selected) => selected.length > 0,
            onRun: (selected) =>
              runBulk(
                selected.map((r) => r.id),
                'SET_ACTIVE',
              ),
          },
          {
            id: 'SET_INACTIVE',
            label: 'Deactivate',
            variant: 'outline',
            isAllowed: (selected) => selected.length > 0,
            onRun: (selected) =>
              runBulk(
                selected.map((r) => r.id),
                'SET_INACTIVE',
              ),
          },
          {
            id: 'SET_ASK_GRATUITY_ON',
            label: 'Enable ask tip',
            variant: 'outline',
            isAllowed: (selected) => selected.length > 0,
            onRun: (selected) =>
              runBulk(
                selected.map((r) => r.id),
                'SET_ASK_GRATUITY_ON',
              ),
          },
          {
            id: 'SET_ASK_GRATUITY_OFF',
            label: 'Disable ask tip',
            variant: 'outline',
            isAllowed: (selected) => selected.length > 0,
            onRun: (selected) =>
              runBulk(
                selected.map((r) => r.id),
                'SET_ASK_GRATUITY_OFF',
              ),
          },
          {
            id: 'SET_ASK_LOYALTY_ON',
            label: 'Enable ask loyalty',
            variant: 'outline',
            isAllowed: (selected) => selected.length > 0,
            onRun: (selected) =>
              runBulk(
                selected.map((r) => r.id),
                'SET_ASK_LOYALTY_ON',
              ),
          },
          {
            id: 'SET_ASK_LOYALTY_OFF',
            label: 'Disable ask loyalty',
            variant: 'outline',
            isAllowed: (selected) => selected.length > 0,
            onRun: (selected) =>
              runBulk(
                selected.map((r) => r.id),
                'SET_ASK_LOYALTY_OFF',
              ),
          },
          {
            id: 'RESET_DEVICE',
            label: 'Reset device',
            variant: 'danger',
            isAllowed: (selected) => selected.length > 0,
            onRun: (selected) => {
              const ok = window.confirm(
                'Reset selected devices? Each gets a new order id and linked payment/refund (device and paired terminal) are cleared.',
              );
              if (!ok) return;
              return runBulk(
                selected.map((r) => r.id),
                'RESET_DEVICE',
              );
            },
          },
        ]}
        rowMuted={(row) => !row.isActive}
        pagination={{
          page: pageInfo?.page ?? url.page,
          pageSize: pageInfo?.pageSize ?? url.size,
          totalRows: pageInfo?.totalRows ?? 0,
          totalPages: pageInfo?.totalPages ?? 1,
          onPageChange: (next) => patch({ page: next }),
          onPageSizeChange: (next) => setFilters({ size: next }),
        }}
      />

      <Modal
        open={createModalOpen}
        onClose={() => (creating ? null : setCreateModalOpen(false))}
        title="Add device"
        description="Creates a checkout device and shows a one-time pairing code for the device."
        footer={
          <>
            <Button
              variant="ghost"
              type="button"
              disabled={creating}
              onClick={() => setCreateModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              id="admin.devices.create"
              type="button"
              disabled={creating || !newDeviceName.trim() || !scopeId}
              onClick={() => void submitCreateDevice()}
            >
              {creating ? 'Creating…' : 'Create & show code'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label="Name" required>
            <Input
              id="admin.devices.name"
              value={newDeviceName}
              onChange={(event) => setNewDeviceName(event.target.value)}
              placeholder="Bar 1"
              autoFocus
              required
            />
          </FormField>
          <FormField label="Category">
            <Select
              value={newDeviceCategoryId}
              onChange={(event) => setNewDeviceCategoryId(event.target.value)}
              options={[
                { value: '', label: 'No category' },
                ...(categoriesData?.categories.map((c) => ({
                  value: c.id,
                  label: c.name,
                })) ?? []),
              ]}
            />
          </FormField>
        </div>
      </Modal>

      <DevicePairingModal
        open={pairingModalOpen}
        pairing={pairingResult}
        onClose={() => setPairingModalOpen(false)}
      />
    </div>
  );
}
